import axios from "axios";
import { apiClient } from "../api/ApiClient";
import type { DashboardResponse } from "../models/Dashboard";
import type { DashboardPeriod } from "../pages/Dashboard/DashboardState";
// Accept either ASP.NET naming policy; never replace a failed request with fake metrics.
function pascalKeys(value: unknown): unknown {
    if (Array.isArray(value))
        return value.map(pascalKeys);
    if (value && typeof value === "object")
        return Object.fromEntries(Object.entries(value).map(([key, v]) => [key.charAt(0).toUpperCase() + key.slice(1), pascalKeys(v)]));
    return value;
}
export async function GetDashboard(period: DashboardPeriod, signal?: AbortSignal, warehouseCode?: string): Promise<DashboardResponse> {
    if (!warehouseCode)
        throw new Error("Select an operating warehouse to load analytics.");
    try {
        const response = await apiClient.get("dashboard/summary", { params: { period, currency: "NGN", warehouse: warehouseCode }, signal, showBusyIndicator: false, showError: false });
        const data = pascalKeys(response.data) as DashboardResponse;
        if (!data || ![data.SalesTotal, data.OrderCount, data.CustomerCount, data.LowStockCount].every(Number.isFinite) || !Array.isArray(data.Sales) || !Array.isArray(data.RecentOrders) || !Array.isArray(data.LowStockItems)
            || !data.Sales.every(x => typeof x.Label === "string" && Number.isFinite(x.Value))
            || !data.RecentOrders.every(x => Number.isFinite(x.Amount) && typeof x.CurrencyCode === "string" && /^[A-Z]{3}$/.test(x.CurrencyCode))
            || ![data.SalesChange, data.OrdersChange, data.CustomersChange].every(x => x == null || Number.isFinite(x)))
            throw new Error("The dashboard endpoint returned an invalid summary. Check the backend response contract.");
        return data;
    }
    catch (error) {
        const normalized = error && typeof error === "object" ? error as {status?:number;message?:string} : undefined;
        const status = axios.isAxiosError(error) ? error.response?.status : normalized?.status;
        if (status === 403)
            throw new Error("Your account cannot access this warehouse. Check its assignment and active status.");
        if (status === 401)
            throw new Error("Your session has expired. Sign in again.");
        if (status === 404)
            throw new Error("The dashboard endpoint is missing. Deploy GET dashboard/summary.");
        if (status === 400)
            throw new Error("The dashboard rejected the period, currency or warehouse. Check the request parameters.");
        if (status && status >= 500)
            throw new Error("The dashboard backend failed. Check the server exception log for this request, then retry.");
        if (!(error instanceof Error) && normalized?.message)
            throw new Error(normalized.message);
        throw error;
    }
}
